import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Truck, CheckCircle2, ArrowRight, ChevronRight, Phone, Calculator as CalculatorIcon, AlertTriangle, Ruler } from 'lucide-react';
import { SERVICE_CITIES, SERVICE_PRODUCT_SLUGS, MATERIAL_GUIDES, CALC_MATERIAL_IDS, getServiceCity, getServiceProduct } from '../data/cityMaterialContent';
import { SimpleAreaCalculator } from '../components/SimpleAreaCalculator';
import { JsonLd, useSEO } from '../utils/seo';
import { NotFound } from './NotFound';
// Delivery tiers collapsed to three rings: inner (former zones 1+2 → +$80),
// middle (zone 3 → +$120, unchanged), outer (former zones 4+5 → +$175).
// Curated city.zone values (1–5) still map cleanly onto the right tier fee.
const ZONE_FEE: Record<number, string> = {
  1: '+$80',
  2: '+$80',
  3: '+$120',
  4: '+$175',
  5: '+$175'
};
// Legacy curated zone (1–5) → the tier number customers actually see. There
// are only three zones, so a city stored as 4 or 5 must display as Zone 3.
const ZONE_TIER: Record<number, 1 | 2 | 3> = {
  1: 1,
  2: 1,
  3: 2,
  4: 3,
  5: 3
};
function ProjectFitForMaterial(material: string): string[] {
  const m = material.toLowerCase();
  if (m.includes('topsoil') || m.includes('compost')) return ['New lawn install or overseeding', 'Raised beds and garden plots', 'Leveling low spots in the yard'];
  if (m.includes('bark') || m.includes('mulch')) return ['Refreshing flower beds and tree rings', 'Pathways and play areas', 'Reducing weeds and retaining moisture'];
  if (m.includes('drain')) return ['French drains and footing drains', 'Behind retaining walls', 'Crushed rock alternatives where drainage matters'];
  if (m.includes('minus') || m.includes('crushed')) return ['Driveways and parking pads', 'Compacted base for patios and walkways', 'General-purpose road base'];
  if (m.includes('pea gravel')) return ['Patios, walkways, and dog runs', 'Pool decks and play areas', 'Decorative groundcover'];
  return ['Driveway base and resurfacing', 'General landscape and grading', 'Filling and grading project sites'];
}
// Tabbed "Explore" panel — replaces what used to be two stacked grid
// sections on the page. Renders the same links (SEO unchanged) in one
// shorter block that lets users switch between "other materials in this
// city" and "this material in other cities" with a single tap.
function ExploreBlock({
  city,
  product,
  otherMaterials,
  otherCities
}: {
  city: any;
  product: any;
  otherMaterials: {
    product: any;
    category: any;
  }[];
  otherCities: any[];
}) {
  const [tab, setTab] = useState<'materials' | 'cities'>('materials');
  return <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 border-t border-stone-200 dark:border-white/10" data-id="element-2631" data-mp="12eivacxe6z27">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6" data-id="element-2632" data-mp="yaumxs0xrzfw">
        <h2 className="font-heading text-2xl font-extrabold text-stone-900 dark:text-white tracking-tight" data-id="element-2633" data-mp="te49zdkfhjyp">
          Explore
        </h2>
        <div className="inline-flex rounded-xl bg-stone-100 dark:bg-white/[0.04] p-1 border border-stone-200 dark:border-white/10" role="tablist" data-id="element-2634" data-mp="352iw0o2fbkh3">
          <button type="button" role="tab" aria-selected={tab === 'materials'} onClick={() => setTab('materials')} className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-colors ${tab === 'materials' ? 'bg-white dark:bg-white/10 text-brand-orange shadow-sm' : 'text-stone-600 dark:text-stone-400 hover:text-stone-900 dark:hover:text-white'}`} data-id="element-2635" data-mp="3e0uoywqriucp">
            Other materials in {city.name}
          </button>
          <button type="button" role="tab" aria-selected={tab === 'cities'} onClick={() => setTab('cities')} className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-colors ${tab === 'cities' ? 'bg-white dark:bg-white/10 text-brand-orange shadow-sm' : 'text-stone-600 dark:text-stone-400 hover:text-stone-900 dark:hover:text-white'}`} data-id="element-2636" data-mp="2v9tk5s2yo4l8">
            {product.name} in other cities
          </button>
        </div>
      </div>

      {tab === 'materials' ? <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-2637" data-mp="93gwnpivqgvi">
          {otherMaterials.map(({
        product: p
      }) => <Link key={p.slug} to={`/gravel-delivery/${city.slug}/${p.slug}`} className="group p-5 rounded-2xl bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/10 hover:border-brand-orange transition-colors" data-id="element-2638" data-mp="muvevu4yqe1i">
              <div className="flex items-start justify-between gap-3" data-id="element-2639" data-mp="wr8bp0vt180h">
                <div data-id="element-2640" data-mp="rsn7kqdfm46r">
                  <h3 className="font-heading text-base font-bold text-stone-900 dark:text-white group-hover:text-brand-orange transition-colors" data-id="element-2641" data-mp="rt6p45z66qk9">
                    {p.name}
                  </h3>
                  <p className="mt-1 text-xs text-stone-500 dark:text-stone-400" data-id="element-2642" data-mp="11se1oqy2yh6b">
                    ${p.pricePerYard}/yd · Delivered to {city.name}
                  </p>
                </div>
                <ArrowRight className="w-4 h-4 text-stone-400 group-hover:text-brand-orange group-hover:translate-x-0.5 transition-all shrink-0" data-id="element-2643" data-mp="33kkdcgz0beki" />
              </div>
            </Link>)}
        </div> : <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-id="element-2644" data-mp="2xfmalx73x48z">
          {otherCities.map(c => <Link key={c.slug} to={`/gravel-delivery/${c.slug}/${product.slug}`} className="group flex items-center justify-between gap-2 px-4 py-3 rounded-xl bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/10 hover:border-brand-orange transition-colors" data-id="element-2645" data-mp="2jopxlepnyvn1">
              <span className="text-sm font-bold text-stone-800 dark:text-white group-hover:text-brand-orange transition-colors" data-id="element-2646" data-mp="2eqobekwxyg7c">
                {c.name}
              </span>
              <ArrowRight className="w-3.5 h-3.5 text-stone-400 group-hover:text-brand-orange transition-colors" data-id="element-2647" data-mp="25pgfg9hwdrgf" />
            </Link>)}
        </div>}
    </section>;
}
export function CityMaterial() {
  const navigate = useNavigate();
  const {
    citySlug = '',
    materialSlug = ''
  } = useParams<{
    citySlug: string;
    materialSlug: string;
  }>();
  const city = getServiceCity(citySlug);
  // Only the curated featured materials get a city landing page. Without this
  // guard every one of the 32 products paired with every city, generating
  // ~364 thin, near-duplicate pages that competed with the real /shop/:slug
  // product pages and burned crawl budget. Anything outside the curated set
  // now resolves to the 404 page instead of rendering indexable filler.
  const isFeaturedMaterial = SERVICE_PRODUCT_SLUGS.includes(materialSlug);
  const productEntry = isFeaturedMaterial ? getServiceProduct(materialSlug) : null;
  const seoProduct = productEntry?.product;
  useSEO({
    title: city && seoProduct ? `${seoProduct.name} Delivery in ${city.name}, WA | 206Gravel` : 'Gravel Delivery Service Areas | 206Gravel',
    description: city && seoProduct ? `${city.blurb} Order ${seoProduct.name.toLowerCase()} by the cubic yard with same-day or next-day delivery to ${city.name}, Washington.` : 'Browse bulk gravel, rock, soil, bark, and aggregate delivery across Seattle, King County, and Pierce County.',
    image: seoProduct?.image,
    path: city && seoProduct ? `/gravel-delivery/${city.slug}/${seoProduct.slug}` : '/service-areas'
  });
  if (!city || !productEntry) return <NotFound data-id="element-2648" data-mp="1lfvuyyx8f0vs" />;
  const {
    product,
    category
  } = productEntry;
  const zoneFee = ZONE_FEE[city.zone] || 'Custom Quote';
  const projectFits = ProjectFitForMaterial(product.name);
  // Other materials in this city — exclude the current one.
  const otherMaterials = SERVICE_PRODUCT_SLUGS.filter(s => s !== materialSlug).map(s => getServiceProduct(s)).filter(Boolean) as {
    product: any;
    category: any;
  }[];
  // Other cities for this material — exclude the current one.
  const otherCities = SERVICE_CITIES.filter(c => c.slug !== city.slug);
  // Build the estimator deep link — preselects address, material, and the
  // standard full-load yardage so the user lands on a ready-to-quote form.
  const estimatorHref = `/?address=${encodeURIComponent(city.geocodeHint)}&material=${encodeURIComponent(product.name)}&qty=12#estimator`;
  const heroTitle = `${product.name} Delivery in ${city.name}, ${city.state}`;
  const description = `${city.blurb} Order ${product.name.toLowerCase()} by the cubic yard with same-day or next-day delivery to ${city.name}, Washington.`;
  const path = `/gravel-delivery/${city.slug}/${product.slug}`;
  // Material-specific editorial: explainer, depths, caution, FAQs. Present for
  // every curated material; the older generic layout stays as a fallback.
  const guide = MATERIAL_GUIDES[product.slug];
  const guideFaqs = guide ? guide.faqs(city.name, product.pricePerYard) : [];
  const applyYards = (yards: number) => {
    navigate(`/?address=${encodeURIComponent(city.geocodeHint)}&material=${encodeURIComponent(product.name)}&qty=${yards}#estimator`);
  };
  return <main className="bg-[#fdfbf7] dark:bg-[#222222] min-h-screen" data-id="element-2649" data-mp="1s2g3jz42d0kx">
      <JsonLd data={[{
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: `${product.name} delivery in ${city.name}, WA`,
      description,
      image: product.image,
      category: category.name,
      offers: {
        '@type': 'Offer',
        priceCurrency: 'USD',
        price: product.pricePerYard,
        url: `https://206gravel.com${path}`,
        availability: 'https://schema.org/InStock'
      }
    }, {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [{
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://206gravel.com/'
      }, {
        '@type': 'ListItem',
        position: 2,
        name: 'Service Areas',
        item: 'https://206gravel.com/service-areas'
      }, {
        '@type': 'ListItem',
        position: 3,
        name: `${product.name} in ${city.name}`,
        item: `https://206gravel.com${path}`
      }]
    }, ...(guideFaqs.length ? [{
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: guideFaqs.map(f => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: {
          '@type': 'Answer',
          text: f.a
        }
      }))
    }] : [])]} data-id="element-2650" data-mp="1s03nxsls32b1" />
      {/* Breadcrumbs */}
      <nav aria-label="Breadcrumb" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-6" data-id="element-2651" data-mp="1ucp4xjj7m759">
        <ol className="flex items-center gap-1.5 text-xs text-stone-500 dark:text-stone-400" data-id="element-2652" data-mp="1zc1s65dztg8d">
          <li data-id="element-2653" data-mp="1kfam5d9td69s">
            <Link to="/" className="hover:text-brand-orange transition-colors" data-id="element-2654" data-mp="1kiyiek6n86s7">
              Home
            </Link>
          </li>
          <ChevronRight className="w-3 h-3" data-id="element-2655" data-mp="1bggum1bcb633" />
          <li data-id="element-2656" data-mp="1bhrye14scdu7">
            <Link to="/service-areas" className="hover:text-brand-orange transition-colors" data-id="element-2657" data-mp="1bi0es304a1a5">
              Service Areas
            </Link>
          </li>
          <ChevronRight className="w-3 h-3" data-id="element-2658" data-mp="35uzx43vvt9l4" />
          <li data-id="element-2659" data-mp="35rvpewj7jvpp">
            <Link to={`/gravel-delivery/${city.slug}`} className="hover:text-brand-orange transition-colors" data-id="element-2660" data-mp="3at1dui5u4wtf">
              {city.name}
            </Link>
          </li>
          <ChevronRight className="w-3 h-3" data-id="element-2661" data-mp="3kx82jkmja0py" />
          <li className="text-stone-900 dark:text-white font-semibold truncate" aria-current="page" data-id="element-2662" data-mp="3fyf0cocp5w53">
            {product.name}
          </li>
        </ol>
      </nav>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-12" data-id="element-2663" data-mp="3oytnzud4vnbr">
        <div className="grid md:grid-cols-2 gap-10 items-center" data-id="element-2664" data-mp="3owqnuhi9odtb">
          <motion.div initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} data-id="element-2665" data-mp="7gsecxjho6hm">
            <div className="inline-flex items-center gap-2 mb-3" data-id="element-2666" data-mp="ghgqfmpbnwrv">
              <MapPin className="w-3.5 h-3.5 text-brand-orange" data-id="element-2667" data-mp="chg31ck8wb8o" />
              <span className="text-[11px] font-bold text-brand-orange uppercase tracking-[0.14em]" data-id="element-2668" data-mp="cf24uuu9qiww">
                {city.name}, {city.state} · {city.county} County · Zone{' '}
                {ZONE_TIER[city.zone]}
              </span>
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-stone-900 dark:text-white leading-[1.05]" data-id="element-2669" data-mp="h8yp0c442yu2">
              {heroTitle}
            </h1>
            <p className="mt-4 text-stone-600 dark:text-stone-300 leading-relaxed" data-id="element-2670" data-mp="r5tiuhpfwb20">
              {city.blurb} Order {product.name.toLowerCase()} by the cubic yard
              with same-day or next-day delivery to {city.name} and surrounding
              neighborhoods including{' '}
              {city.neighborhoods.slice(0, 3).join(', ')}, and{' '}
              {city.neighborhoods[city.neighborhoods.length - 1]}.
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-3" data-id="element-2671" data-mp="19xckol7q9j2g">
              <Link to={estimatorHref} className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-brand-orange hover:bg-brand-orange-hover text-white font-bold text-sm transition-colors shadow-lg shadow-brand-orange/20" data-id="element-2672" data-mp="1eumc26eog4sx">
                <CalculatorIcon className="w-4 h-4" data-id="element-2673" data-mp="1nwlu921fhexg" />
                Get an Instant Estimate
                <ArrowRight className="w-4 h-4" data-id="element-2674" data-mp="38fpjruhyzsgr" />
              </Link>
              <a href="tel:206-650-0129" className="inline-flex items-center gap-2 px-5 py-3 rounded-xl border border-stone-300 dark:border-white/15 text-stone-900 dark:text-white font-bold text-sm hover:bg-stone-100 dark:hover:bg-white/5 transition-colors" data-id="element-2675" data-mp="34bq4tgcc91pi">
                <Phone className="w-4 h-4" data-id="element-2676" data-mp="3s2dqr5j39rv2" />
                (206) 650-0129
              </a>
            </div>

            <div className="mt-6 grid grid-cols-3 gap-3 text-xs" data-id="element-2677" data-mp="3n3j1pluj372i">
              <div className="px-3 py-2.5 rounded-lg bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/10" data-id="element-2678" data-mp="3n5dl2vq5ir4h">
                <div className="text-[10px] uppercase tracking-wider text-stone-500 dark:text-stone-400 font-bold mb-0.5" data-id="element-2679" data-mp="3ddqzq6k3nbo0">
                  Price
                </div>
                <div className="font-bold text-stone-900 dark:text-white" data-id="element-2680" data-mp="epk5qk7k3lp0">
                  ${product.pricePerYard}/yd
                </div>
              </div>
              <div className="px-3 py-2.5 rounded-lg bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/10" data-id="element-2681" data-mp="3qj2h526vmfst">
                <div className="text-[10px] uppercase tracking-wider text-stone-500 dark:text-stone-400 font-bold mb-0.5" data-id="element-2682" data-mp="3ujm9lsgi8v8m">
                  Delivery
                </div>
                <div className="font-bold text-stone-900 dark:text-white" data-id="element-2683" data-mp="3lltv2qilxrma">
                  {zoneFee}
                </div>
              </div>
              <div className="px-3 py-2.5 rounded-lg bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/10" data-id="element-2684" data-mp="d7laa4mbu54o">
                <div className="text-[10px] uppercase tracking-wider text-stone-500 dark:text-stone-400 font-bold mb-0.5" data-id="element-2685" data-mp="3g7di4lrfjf8">
                  Lead Time
                </div>
                <div className="font-bold text-stone-900 dark:text-white" data-id="element-2686" data-mp="w439t87f39bh">
                  Same-day*
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="relative rounded-2xl overflow-hidden border border-stone-200 dark:border-white/10 bg-white dark:bg-white/[0.04] aspect-[4/3]" data-id="element-2687" data-mp="10wml1yimtsd4">
            {product.image ? <img src={product.image} alt={`${product.name} delivered in ${city.name}, WA`} className="w-full h-full object-cover" loading="eager" data-id="element-2688" data-mp="2qcgq9d72gzhb" /> : <div className="w-full h-full bg-stone-100 dark:bg-stone-800" data-id="element-2689" data-mp="1xn9m9youfhoo" />}
            <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between" data-id="element-2690" data-mp="1xpcln9bvyn62">
              <span className="px-3 py-1 rounded-full bg-black/70 backdrop-blur-md text-white text-[11px] font-bold" data-id="element-2691" data-mp="2bky5l239igrm">
                {category.name}
              </span>
              <Link to={`/shop/${product.slug}`} className="px-3 py-1 rounded-full bg-brand-orange text-white text-[11px] font-bold hover:bg-brand-orange-hover transition-colors" data-id="element-2692" data-mp="3n0whddptrgwj">
                View Material
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Material explainer — what it actually is, what it's for, how deep to
          place it, and the mistake customers make. This is the substance the
          page competes on; the catalog blurb alone left it thinner than the
          supplier pages ranking for the same query. */}
      {guide && <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 border-t border-stone-200 dark:border-white/10" data-id="element-2693" data-mp="1x0iiki75w31p">
          <div className="grid lg:grid-cols-5 gap-8" data-id="element-2694" data-mp="20ypxd7i2eua9">
            <div className="lg:col-span-3" data-id="element-2695" data-mp="25zngbmawfsct">
              <h2 className="font-heading text-2xl font-extrabold text-stone-900 dark:text-white tracking-tight" data-id="element-2696" data-mp="25xss9z4bzk4i">
                What is {product.name}?
              </h2>
              <p className="mt-3 text-stone-600 dark:text-stone-300 leading-relaxed" data-id="element-2697" data-mp="1dagcq8wl8ckp">
                {guide.what}
              </p>
              <h3 className="mt-8 font-heading text-lg font-bold text-stone-900 dark:text-white" data-id="element-2698" data-mp="1i7ye9e89zpio">
                Why it works for {city.name} projects
              </h3>
              <p className="mt-2 text-stone-600 dark:text-stone-300 leading-relaxed" data-id="element-2699" data-mp="15s8gcponrag0">
                {guide.localAngle(city.name)}
              </p>
              <div className="mt-6 flex items-start gap-3 p-5 rounded-2xl bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20" data-id="element-2700" data-mp="wram2z40uham">
                <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5 text-amber-600 dark:text-amber-400" data-id="element-2701" data-mp="rt8v7s0ctdol" />
                <div data-id="element-2702" data-mp="mz4l4zvskpja">
                  <h3 className="font-heading text-base font-bold text-stone-900 dark:text-white" data-id="element-2703" data-mp="i24g8saohstr">
                    {guide.caution.title}
                  </h3>
                  <p className="mt-1 text-sm text-stone-700 dark:text-stone-300 leading-relaxed" data-id="element-2704" data-mp="d4mcrhffk5t5">
                    {guide.caution.body}
                  </p>
                </div>
              </div>
            </div>

            <div className="lg:col-span-2" data-id="element-2705" data-mp="44g366murpz9">
              <h3 className="font-heading text-lg font-bold text-stone-900 dark:text-white" data-id="element-2706" data-mp="1ryrs5satyv9m">
                Best uses
              </h3>
              <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-2" data-id="element-2707" data-mp="1wxcqtnafp6vy">
                {guide.uses.map(use => <li key={use} className="flex items-start gap-2.5 px-4 py-2.5 rounded-xl bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/10" data-id="element-2708" data-mp="1j0fuj6bjqx7o">
                    <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5 text-brand-orange" data-id="element-2709" data-mp="197rywewayzaj" />
                    <span className="text-sm font-medium text-stone-800 dark:text-stone-200" data-id="element-2710" data-mp="1e0bhxrdremmq">
                      {use}
                    </span>
                  </li>)}
              </ul>

              <h3 className="mt-8 font-heading text-lg font-bold text-stone-900 dark:text-white inline-flex items-center gap-2" data-id="element-2711" data-mp="14aia91gqmrnr">
                <Ruler className="w-4 h-4 text-brand-orange" data-id="element-2712" data-mp="qah9dm0h3k8a" />
                How deep to place it
              </h3>
              <dl className="mt-3 rounded-2xl bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/10 divide-y divide-stone-200 dark:divide-white/10" data-id="element-2713" data-mp="valfqj7o2yks">
                {guide.depths.map(row => <div key={row.job} className="flex items-baseline justify-between gap-4 px-4 py-3" data-id="element-2714" data-mp="6q0bq8ezgop9">
                    <dt className="text-sm text-stone-700 dark:text-stone-300" data-id="element-2715" data-mp="klc2y5txjx1f">
                      {row.job}
                    </dt>
                    <dd className="text-sm font-bold text-stone-900 dark:text-white text-right shrink-0" data-id="element-2716" data-mp="ulbemblnc3ii">
                      {row.depth}
                    </dd>
                  </div>)}
              </dl>
            </div>
          </div>
        </section>}

      {/* What it's for — legacy layout, retained for any material without a
          written guide so the page never renders bare. */}
      {!guide && <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 border-t border-stone-200 dark:border-white/10" data-id="element-2717" data-mp="1dbkpe9vi13lt">
        <div className="grid md:grid-cols-3 gap-8" data-id="element-2718" data-mp="18ekftozfxipy">
          <div className="md:col-span-1" data-id="element-2719" data-mp="1kvziud19efpw">
            <h2 className="font-heading text-2xl font-extrabold text-stone-900 dark:text-white tracking-tight" data-id="element-2720" data-mp="1kuocc5gfitv3">
              What {city.name} customers use{' '}
              <span className="text-brand-orange" data-id="element-2721" data-mp="1utx2soc35qcp">{product.name}</span> for
            </h2>
            <p className="mt-3 text-sm text-stone-600 dark:text-stone-300 leading-relaxed" data-id="element-2722" data-mp="1pv2jzme4c1sl">
              {product.description}
            </p>
          </div>
          <div className="md:col-span-2" data-id="element-2723" data-mp="1261by0976snu">
            <ul className="space-y-3" data-id="element-2724" data-mp="x6omd7qxz7hk">
              {projectFits.map(fit => <li key={fit} className="flex items-start gap-3 p-4 rounded-xl bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/10" data-id="element-2725" data-mp="x881gv0v3chr">
                  <CheckCircle2 className="w-5 h-5 text-brand-orange shrink-0 mt-0.5" data-id="element-2726" data-mp="174b2tynw373n" />
                  <span className="text-sm text-stone-800 dark:text-stone-200 font-medium" data-id="element-2727" data-mp="jegrjlj7xqli">
                    {fit}
                  </span>
                </li>)}
              {product.bestFor && product.bestFor.length > 0 && <li className="flex items-start gap-3 p-4 rounded-xl bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/10" data-id="element-2728" data-mp="2q30rd5sdvpp6">
                  <CheckCircle2 className="w-5 h-5 text-brand-orange shrink-0 mt-0.5" data-id="element-2729" data-mp="2uw27fh4btvac" />
                  <span className="text-sm text-stone-800 dark:text-stone-200 font-medium" data-id="element-2730" data-mp="2h4f97bqtqcox">
                    Also great for: {product.bestFor.join(', ')}
                  </span>
                </li>}
            </ul>
          </div>
        </div>
      </section>}

      {/* Delivery to this city */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 border-t border-stone-200 dark:border-white/10" data-id="element-2731" data-mp="3ds20ywcyd6w1">
        <h2 className="font-heading text-2xl font-extrabold text-stone-900 dark:text-white tracking-tight mb-6" data-id="element-2732" data-mp="2zvepzpo60g74">
          Delivering to {city.name}, {city.state}
        </h2>
        <div className="grid md:grid-cols-2 gap-6" data-id="element-2733" data-mp="1ocluxpubs1ad">
          <div className="p-6 rounded-2xl bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/10" data-id="element-2734" data-mp="1tdjd42fc4vbv">
            <div className="flex items-center gap-3 mb-3" data-id="element-2735" data-mp="3v14dd27jccdq">
              <div className="w-10 h-10 rounded-full bg-brand-orange/10 flex items-center justify-center" data-id="element-2736" data-mp="3q3cjw3r8si7f">
                <Truck className="w-5 h-5 text-brand-orange" data-id="element-2737" data-mp="3lc47cyum8gq1" />
              </div>
              <h3 className="font-heading text-lg font-bold text-stone-900 dark:text-white" data-id="element-2738" data-mp="3har3uvezwpma">
                Zone {ZONE_TIER[city.zone]} — {zoneFee} delivery
              </h3>
            </div>
            <p className="text-sm text-stone-600 dark:text-stone-300 leading-relaxed" data-id="element-2739" data-mp="3c9bjz6ok0p5h">
              {city.name} sits in our Zone {ZONE_TIER[city.zone]} delivery
              area. Our standard{' '}
              {ZONE_TIER[city.zone] === 1 ? 'inner-zone' : ZONE_TIER[city.zone] === 2 ? 'mid-zone' : 'outer-zone'}{' '}
              rate applies — billed per truckload, not per yard.
            </p>
            <Link to={`/delivery-zones?address=${encodeURIComponent(city.geocodeHint)}`} className="inline-flex items-center gap-1.5 mt-3 text-xs font-bold text-brand-orange hover:underline" data-id="element-2740" data-mp="10jsmwrql0phr">
              See {city.name} on the zone map
              <ArrowRight className="w-3 h-3" data-id="element-2741" data-mp="2jggg7yw20a53" />
            </Link>
          </div>
          <div className="p-6 rounded-2xl bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/10" data-id="element-2742" data-mp="2o7g98t21y82b">
            <h3 className="font-heading text-lg font-bold text-stone-900 dark:text-white mb-3" data-id="element-2743" data-mp="2s8i9nd7pgq5v">
              Neighborhoods we cover
            </h3>
            <div className="flex flex-wrap gap-2" data-id="element-2744" data-mp="1vlndv4j35vpk">
              {city.neighborhoods.map(n => <span key={n} className="px-3 py-1 rounded-full bg-stone-100 dark:bg-white/5 border border-stone-200 dark:border-white/10 text-xs font-medium text-stone-700 dark:text-stone-300" data-id="element-2745" data-mp="20inkbgjul0h7">
                  {n}
                </span>)}
            </div>
          </div>
        </div>
      </section>

      {/* Yardage calculator — the same engine used on the product page, with
          the material already chosen, so "how much do I need" is answered on
          the page instead of one navigation away. */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 border-t border-stone-200 dark:border-white/10" data-id="element-2746" data-mp="3jj6476ti8gvf">
        <div className="grid lg:grid-cols-3 gap-8" data-id="element-2747" data-mp="3shi0nph1y8vs">
          <div className="lg:col-span-1" data-id="element-2748" data-mp="3sjb7p5vkuv4b">
            <h2 className="font-heading text-2xl font-extrabold text-stone-900 dark:text-white tracking-tight" data-id="element-2749" data-mp="3og6aniu9nln2">
              How much {product.name} do I need?
            </h2>
            <p className="mt-3 text-stone-600 dark:text-stone-300 leading-relaxed" data-id="element-2750" data-mp="68gqldf2xrue">
              Enter the area you're covering and how deep you want it. We'll
              convert it to cubic yards and round to a load we can actually
              deliver to {city.name} — then carry the number straight into your
              delivered estimate.
            </p>
            <p className="mt-4 text-sm text-stone-500 dark:text-stone-400" data-id="element-2751" data-mp="p1t45amu8a9p">
              ${product.pricePerYard}/yd · {zoneFee} delivery · 3-yard minimum
            </p>
          </div>
          <div className="lg:col-span-2 p-6 rounded-2xl bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/10" data-id="element-2752" data-mp="13orqir6u8ehk">
            <SimpleAreaCalculator materialSlug={CALC_MATERIAL_IDS[product.slug] || product.slug} onApply={applyYards} data-id="element-2753" data-mp="18m1ncrwk3sff" />
          </div>
        </div>
      </section>

      {/* Material-specific FAQs — also emitted as FAQPage schema above. */}
      {guideFaqs.length > 0 && <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 border-t border-stone-200 dark:border-white/10" data-id="element-2754" data-mp="kw7c2ervybxa">
          <h2 className="font-heading text-2xl font-extrabold text-stone-900 dark:text-white tracking-tight" data-id="element-2755" data-mp="ppjs40ii1aef">
            {product.name} questions, answered
          </h2>
          <div className="mt-6 grid md:grid-cols-2 gap-4" data-id="element-2756" data-mp="fz76s62v1d78">
            {guideFaqs.map(faq => <div key={faq.q} className="p-6 rounded-2xl bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/10" data-id="element-2757" data-mp="1xusdz3cljng">
                <h3 className="font-heading text-base font-bold text-stone-900 dark:text-white" data-id="element-2758" data-mp="6wxx1iff1nuw">
                  {faq.q}
                </h3>
                <p className="mt-2 text-sm text-stone-600 dark:text-stone-300 leading-relaxed" data-id="element-2759" data-mp="23vwvtmx3a12c">
                  {faq.a}
                </p>
              </div>)}
          </div>
        </section>}

      {/* Explore — single tabbed block that combines "other materials" and
                    "other cities" so the page doesn't feel like a sitemap of stacked
                    identical link grids. Same SEO links, half the scroll. */}
      <ExploreBlock city={city} product={product} otherMaterials={otherMaterials} otherCities={otherCities} data-id="element-2760" data-mp="2mnhb5n87ocxt" />

      {/* Final CTA */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 border-t border-stone-200 dark:border-white/10" data-id="element-2761" data-mp="2iizuhwx5efr9">
        <div className="p-8 md:p-10 rounded-2xl bg-stone-900 dark:bg-black border border-stone-800 dark:border-white/10 text-center" data-id="element-2762" data-mp="2do2o6xr96ki2">
          <h2 className="font-heading text-2xl sm:text-3xl font-extrabold text-white tracking-tight" data-id="element-2763" data-mp="126b8helgpe8u">
            Ready to schedule {product.name} delivery to {city.name}?
          </h2>
          <p className="mt-3 text-stone-300 max-w-2xl mx-auto text-sm md:text-base" data-id="element-2764" data-mp="3ishm1i06wyuw">
            Get an instant estimate — no signup, no calls required. Or call
            dispatch directly to skip the form.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3" data-id="element-2765" data-mp="3rmcqsoxegna0">
            <Link to={estimatorHref} className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-brand-orange hover:bg-brand-orange-hover text-white font-bold text-sm transition-colors" data-id="element-2766" data-mp="ft8kop8hv1o">
              <CalculatorIcon className="w-4 h-4" data-id="element-2767" data-mp="aco8nc15o3i2" />
              Get Instant Estimate
            </Link>
            <a href="tel:206-650-0129" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-white/10 hover:bg-white/15 text-white font-bold text-sm transition-colors" data-id="element-2768" data-mp="ef2odn9j2n54">
              <Phone className="w-4 h-4" data-id="element-2769" data-mp="2g5w88mqjtqix" />
              (206) 650-0129
            </a>
          </div>
          <p className="mt-5 text-[11px] text-stone-500" data-id="element-2770" data-mp="1x9nlfsn97ifa">
            *Same-day delivery subject to truck availability. Standard hours
            Mon–Sat.
          </p>
        </div>
      </section>
    </main>;
}